import React, { useState } from "react";
import {
  ToggleButtonGroup,
  ToggleButton,
  Box,
  TextField,
  Button,
  InputAdornment,
  SelectChangeEvent,
  Menu,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";
import { Download, Search, Menu as MenuIcon } from "@mui/icons-material";
import AccelDatePicker from "../AccelDatePicker/AccelDatePicker";

interface ComponentBarProps {
  values?: Array<{ value: string; label: string; icon?: React.ElementType }>;
  toggleType?: string;
  setToggleType?: (type: string) => void;
  showToggleGroup?: boolean;
  showButtons?: boolean;
  downloadPopup?: boolean;
  setDownloadPopup?: React.Dispatch<React.SetStateAction<boolean>>;
  showDownloadButton?: boolean;
  onSearch?: (searchTerm: string) => void;
  selectedAccount?: string;
  onAccountChange?: (event: SelectChangeEvent<string>) => void;
  searchTerm?: string;
  onSearchChange?: (newSearchTerm: string) => void;
  onDownload?: () => void;
  showSearchBar?: boolean;
  showDatePicker?: boolean;
}

const BarComponent: React.FC<ComponentBarProps> = ({
  values = [],
  toggleType,
  setToggleType,
  showToggleGroup,
  showButtons,
  setDownloadPopup,
  showDownloadButton,
  searchTerm,
  onSearchChange,
  onDownload,
  showSearchBar = true,
  showDatePicker = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { theme } = useAccelContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('md'));

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newToggleType: string | null
  ) => {
    if (newToggleType !== null && setToggleType) {
      setToggleType(newToggleType);
    }
  };

  const handleDownload = () => {

    if (setDownloadPopup) {
      setDownloadPopup(true);
    } else if (onDownload) {
      onDownload();
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value: string) => {
    if (setToggleType) {
      setToggleType(value);
    }
    handleMenuClose();
  };

  return (
    <Box
      width="100%"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {showToggleGroup && (
        <>
          {isMobile ? (
            <>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
                sx={{ color: theme?.secondary?.hex || "#000000" }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                {values.map((value) => (
                  <MenuItem
                    key={value.value}
                    onClick={() => handleMenuItemClick(value.value)}
                    selected={toggleType === value.value}
                  >
                    {value.icon && <value.icon sx={{ marginRight: 1 }} />}
                    {value.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <ToggleButtonGroup
              value={toggleType}
              exclusive
              onChange={handleChange}
              aria-label="report type"
              sx={{ height: 24 }}
            >
              {values.map((value) => (
                <ToggleButton
                  key={value.value}
                  value={value.value}
                  aria-label={value.label}
                  sx={{
                    color: theme?.secondary?.hex || "#000000",
                    borderColor: theme?.secondary?.hex || "#000000",
                    borderRadius: "20px",
                    height: "100%",
                    "&.Mui-selected": {
                      color: theme?.secondary?.hex || "#000000",
                      background: `conic-gradient(from 246.83deg at 100%, ${theme?.accent?.hex || "#000000"} 0%, ${theme?.primary?.hex || "#000000"} 50%, ${theme?.accent?.hex || "#000000"} 100%)`,
                    },
                    "&.Mui-selected:hover": {
                      background: `conic-gradient(from 246.83deg at 100%, ${theme?.accent?.hex || "#000000"} 0%, ${theme?.primary?.hex || "#000000"} 50%, ${theme?.accent?.hex || "#000000"} 100%)`,
                    },
                  }}
                >
                  {value.icon && <value.icon sx={{ marginRight: 1 }} />}
                  {value.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </>
      )}
      
      {showButtons && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {showDatePicker && <AccelDatePicker />}
          {!isSmallScreen && (
            <>
              {showDownloadButton && (
                <Button
                  endIcon={
                    <Download sx={{ color: theme?.secondary?.hex || "#000000" }} />
                  }
                  sx={{
                    mx: 1,
                    color: theme?.secondary?.hex || "#000000",
                    textTransform: "none",
                    borderColor: theme?.secondary?.hex || "#000000",
                    height: 36,
                  }}
                  onClick={handleDownload}
                  variant="outlined"
                >
                  Download
                </Button>
              )}

              {showSearchBar && (
                <TextField
                  variant="outlined"
                  placeholder="Search transactions"
                  size="small"
                  value={searchTerm}
                  onChange={(e) =>
                    onSearchChange && onSearchChange(e.target.value)
                  }
                  sx={{
                    ml: 1,
                    borderColor: theme?.secondary?.hex || "#000000",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      height: "36px",
                      "& fieldset": {
                        borderColor: theme?.secondary?.hex || "#000000",
                      },
                      "&:hover fieldset": {
                        borderColor: theme?.secondary?.hex || "#000000",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme?.secondary?.hex || "#000000",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: theme?.text?.hex || "#000000",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: theme?.secondary?.hex || "#000000",
                      opacity: 1,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search
                          sx={{ color: theme?.secondary?.hex || "#000000" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BarComponent;
